import { Toggle } from 'AWS-UI-Components-React';
import React from 'react';
import { ASIN_METRIC_TYPE, ASIN_TYPE, DEFAULT_TREND_WEEKS } from '../../../constants/asin_metric_elements';
import { CHANNELS } from '../../../constants/channels';
import { formatTimeFrameToYYYYMM } from '../../../utils/date-utils';
import logger from '../../../utils/logging';
import { ErrorFlash } from '../../content/error_flash';
import { OPERATION_STATUS, TEXT } from '../common/constants';
import { AsinInfoTable } from './asin_info_table';
import { getAsinMetricsClient } from './asin_metrics_client';
import {
  extractMonth,
  generateGlOrAsinMetricsReportRows,
  getTrendGraphSnapshotMap,
  mergeKeysOfObjectsWithSameId,
} from './asin_metrics_utils';
import { ASINTrendChart } from './asin_trend_chart';
import { GlMetricTable } from './gl_metric_table';
import { HistogramBarChart } from './histogram_bar_chart';

/**
 * Renders an Asin Metric report.
 */
export class AsinMetricReport extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      availableMonths: [],
      glMetricLoadStatus: OPERATION_STATUS.NONE,
      asinInfoLoadStatus: OPERATION_STATUS.NONE,
      glMetricTrendLoadStatus: OPERATION_STATUS.NONE,
      reportRows:[],
      reportSummaryRow: [],
      perUnitAsinRows:[],
      perUnitAsinComponent:'',
      trendDataRows: [],
      trendLines: [],
      displayInMillions: false,
      displayDetails: false,
      latestSnapshot: 0,
      glMetricsMap: null,
    };
    this.payload = {};
  };

  componentDidMount = () => {
    this.initialClientTasks();
  };

  initialClientTasks = () => {
    this.asinMetricsClient = getAsinMetricsClient();
    const payload = this.prepareAsinMetricPayload();
    this.pullGlMetrics(payload);
  };

  pullGlMetrics = (payload) => {
    this.setState({glMetricLoadStatus: OPERATION_STATUS.IN_PROGRESS});
    this.asinMetricsClient.getGlMetrics(this.processGlMetricResults, this.onFailedGLRequest, payload)
        .then(() => this.setState({glMetricLoadStatus: OPERATION_STATUS.SUCESS}));
  };

  pullAsinInfo = (type, bucketId, componentName, asinType) => {
    this.setState({asinInfoLoadStatus: OPERATION_STATUS.IN_PROGRESS, perUnitAsinComponent: componentName});
    //Backend only accepts Retail not RETAIL
    const channel = CHANNELS.RETAIL === this.props.payload.channel ? 'Retail' : this.props.payload.channel;
    let payload = {marketplaceId: parseInt(this.props.payload.marketplace), channel,
      month:formatTimeFrameToYYYYMM(this.props.payload.timeFrame), gls: this.props.payload.gls,
      componentName: componentName, bucketId: bucketId, limit: 10, metric: type, asinType};
    if(this.props.payload.channelType){
      payload.channelType = parseInt(this.props.payload.channelType);
    }
    this.asinMetricsClient.getHistogramAsin(this.processAsinInfo, this.onFailedAsinInfoRequest, payload);
  };

  prepareAsinMetricPayload = () => {
    let channel = this.props.payload.channel;
    if(channel === CHANNELS.RETAIL) channel = 'Retail'; //Backend only accepts Retail not RETAIL
    let asinPayload = Object.assign({}, {channel, month:formatTimeFrameToYYYYMM(this.props.payload.timeFrame),
      gls:this.props.payload.gls, marketplaceId: this.props.payload.marketplace, trendPeriodInWeeks: DEFAULT_TREND_WEEKS,
      metrics: [ASIN_METRIC_TYPE.NET_FA, ASIN_METRIC_TYPE.NET_LA,
                ASIN_METRIC_TYPE.NET_VAR_PER_UNIT,
                ASIN_METRIC_TYPE.ABS_VAR_PER_UNIT,
                ASIN_METRIC_TYPE.WINSORIZED_NET_VAR_PER_UNIT,
                ASIN_METRIC_TYPE.WINSORIZED_ABS_VAR_PER_UNIT],
      asinType: ASIN_TYPE.ALL});
    if(this.props.payload.channelType){
      asinPayload.channelType = parseInt(this.props.payload.channelType);
    }
    this.payload = asinPayload;
    return asinPayload;
  };

  processAsinInfo = (response, type) => {
    if(response && response.data && response.data.asinInfoList) {
      const asinInfoList = response.data.asinInfoList;
      const processedAsinInfoList = asinInfoList.map(info => ({asin: info.id, gl: info.gl, [type]: info.value}));
      this.setState({perUnitAsinRows: processedAsinInfoList, asinInfoLoadStatus: OPERATION_STATUS.SUCESS});
    } else {
      logger.warn('Missing data from /asinInfo api response');
    }
  };

  processGlMetricResults = (response) => {
    this.processTrendGraphData(response);
    if (response.data && response.data.glMetricItems){
      const glMetricItems = response.data.glMetricItems;
      const [reportRows, glMetricsMap, latestSnapshot] = generateGlOrAsinMetricsReportRows(glMetricItems);
      const reportSummaryRow = this.getReportSummaryRow(reportRows);
      this.setState({reportRows, reportSummaryRow, latestSnapshot, glMetricsMap});
    }
  };

  processTrendGraphData = (response) => {
    if (response.data && response.data.glMetricItems) {
      const glMetricItems = response.data.glMetricItems;
      const snapshotMap = getTrendGraphSnapshotMap(glMetricItems);
      // Merge new trend data with existing data (if any) and update available months, trend lines, and trend data rows used for trend graph
      const trendDataRows = [...this.state.trendDataRows];
      const availableMonths = new Set(this.state.availableMonths);
      let uniqueTrendLines = new Set(this.state.trendLines);
      for (const key of snapshotMap.keys()) {
        trendDataRows.push(snapshotMap.get(key));
        Object.keys(snapshotMap.get(key)).filter(k => k !== 'snapshot').forEach(lineId => {
          uniqueTrendLines.add(lineId);
          const month = extractMonth(lineId);
          availableMonths.add(month);
        });
      }
      //Merge keys of trendDataRow with the same snapshotDate
      const combinedRows = mergeKeysOfObjectsWithSameId(trendDataRows, 'snapshot');
      this.setState({trendDataRows: combinedRows, availableMonths: [...availableMonths], trendLines: [...uniqueTrendLines]});
    }
  };

  onFailedGLRequest = () => {
    this.setState({glMetricLoadStatus: OPERATION_STATUS.FAIL});
  };

  onFailedAsinInfoRequest = () => {
    this.setState({asinInfoLoadStatus: OPERATION_STATUS.FAIL});
  };

  //Only display Contribution Profit Component for summary
  getReportSummaryRow = (reportRows) => {
    const summaryRow = reportRows.filter(row => row.component === 'contributionProfit' ||
      row.component === 'contributionProfitWithoutRefunds');
    return summaryRow;
  };

  onToggleMillions = (chosen) => {
    this.setState({ displayInMillions: chosen.detail.checked});
  };

  onToggleDisplayDetails = (chosen) => {
    this.setState({ displayDetails: chosen.detail.checked});
  };

  onCompareMonths = (months) => {
    let pullRawDataCalls = [];
    months.forEach(
      m => {
        let newPayload = Object.assign({}, this.payload);
        newPayload.month = m;
        pullRawDataCalls.push(this.asinMetricsClient.getGlMetrics(this.processTrendGraphData, this.onFailedGLRequest, newPayload));
      }
    );
    this.setState({glMetricTrendLoadStatus: OPERATION_STATUS.IN_PROGRESS});
    Promise.all(pullRawDataCalls).then(() => this.setState({glMetricTrendLoadStatus: OPERATION_STATUS.SUCESS}));
  };

  render() {

    return (
      <div>
        <div className="awsui-util-action-stripe">
          <div className="awsui-util-action-stripe-title" />
          <div className="awsui-util-action-stripe-group">
            <div className="awsui-util-p-s">
              <Toggle
                label="Display All Components"
                checked={this.state.displayDetails}
                disabled={this.state.glMetricLoadStatus === OPERATION_STATUS.IN_PROGRESS}
                onChange={this.onToggleDisplayDetails}
              />
            </div>
            <div className="awsui-util-p-s">
              <Toggle
                label={TEXT.DISPLAY_IN_MILLIONS}
                checked={this.state.displayInMillions}
                disabled={this.state.glMetricLoadStatus === OPERATION_STATUS.IN_PROGRESS}
                onChange={this.onToggleMillions}
              />
            </div>
          </div>
        </div>
        {this.state.glMetricLoadStatus === OPERATION_STATUS.FAIL && (<ErrorFlash type="GL Metrics" />)}
        <GlMetricTable tableName="Contribution Profit Summary"
                       items={this.state.reportSummaryRow}
                       loading={this.state.glMetricLoadStatus === OPERATION_STATUS.IN_PROGRESS}
                       displayInMillions={this.state.displayInMillions}
                       payload={this.props.payload}
                       downloadEnabled={false}
        />
        { this.state.displayDetails && (
          <GlMetricTable tableName="All Components"
                         items={ this.state.reportRows }
                         loading={ this.state.glMetricLoadStatus === OPERATION_STATUS.IN_PROGRESS}
                         displayInMillions={ this.state.displayInMillions }
                         payload={this.props.payload}
                         downloadEnabled={true}
                         latestSnapshot={this.state.latestSnapshot}
          />
        )}
        <ASINTrendChart data={this.state.trendDataRows} metricName="GL Metric"
                        metricsMap={this.state.glMetricsMap}
                        loading={this.state.glMetricLoadStatus === OPERATION_STATUS.IN_PROGRESS
                        || this.state.glMetricTrendLoadStatus === OPERATION_STATUS.IN_PROGRESS}
                        lines={this.state.trendLines} availableMonths={this.state.availableMonths}
                        displayInMillions={this.state.displayInMillions}
                        payload={this.props.payload}
                        enableCompareMonths={true}
                        enableStabilization={true}
                        onCompareMonths={this.onCompareMonths} />
        <HistogramBarChart histogramName="Per Unit Variance ASIN Histogram"
                           payload={this.props.payload}
                           pullAsinInfo={this.pullAsinInfo} />
        <AsinInfoTable tableName={"Sample ASIN Info"} items={this.state.perUnitAsinRows}
                       loadStatus={this.state.asinInfoLoadStatus}
                       displayInMillions={this.state.displayInMillions}
                       payload={this.props.payload}
                       component={this.state.perUnitAsinComponent} />
      </div>
    );
  };
}



// WEBPACK FOOTER //
// ./src/components/reports/asin_metrics/asin_metric_report.js